/* The scroll (LANDING_SPEC.md section 5): one camera through one space. Every value here is a pure function
   of the scroll position, so every scene reverses exactly. Native scrolling is kept (no hijack); the drawn
   position eases toward the real one, which smooths wheel steps without taking the page from the reader.

   Two layouts, one set of scenes:
   - stage: a desktop in landscape with motion allowed. The stage holds (CSS sticky) for STORY viewport
     heights while the scenes play over the fixed glass mark and the star field;
   - flow: phones, portrait screens, reduced motion and no JavaScript. The scenes follow each other; each
     one plays as it enters the viewport (reduced motion: its final still, faded in). */
import type { Mark } from './mark';
import type { Space } from './space';
import { levelBand, levelFlare, sweepEase, sweepLine } from '../src/light/fieldDraw';
import { MODULES } from '../src/modules/registry';
import { day, MINUS, price } from '../src/lib/format';
import { data as DATA } from '../src/tokens/semantic';

export interface PublicLanding {
  symbol?: string;
  session?: string;
  close?: number;
  levels?: { call_wall?: number; put_wall?: number; flip?: number };
  intensity?: { call_wall?: number; put_wall?: number };
  futures?: { symbol: string; ratio: number; call_wall: number };
}
interface CoreLayout {
  chart: { x0: number; x1: number; y0: number; y1: number };
  levels: Partial<Record<LevelKey, { price: number; y: number }>>;
}
type LevelKey = 'call_wall' | 'flip' | 'put_wall';
type Side = 'above' | 'inside' | 'below';

/** scene ranges in viewport heights of scroll, from the moment the stage holds */
const SC = { s2: [0, 1.3], s3: [1.3, 2.3], s4: [2.3, 3.3], s5: [3.3, 4.4], s6: [4.4, 5.6], s7: [5.6, 6.3], s8: [6.3, 6.9] } as const;
export const STORY = 6.9;

const LEVELS: { key: LevelKey; side: Side }[] = [
  { key: 'call_wall', side: 'above' }, { key: 'flip', side: 'inside' }, { key: 'put_wall', side: 'below' },
];
/** where the beam enters the Frame and where the spectrum leaves it, in the Frame's own units (y up) */
const HIT = { x: -0.725, y: -0.1 }, EXIT = { x: 0.811, y: -0.25 };

const clamp = (x: number, a = 0, b = 1) => Math.max(a, Math.min(b, x));
const smooth = (x: number) => { const c = clamp(x); return c * c * (3 - 2 * c); };
const expoOut = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const local = (T: number, [a, b]: readonly [number, number]) => (T - a) / (b - a);
/** visible inside [a, b] with short fades at both ends */
const window_ = (T: number, a: number, b: number, f = 0.12) => clamp((T - a) / f + 1) * clamp((b - T) / f + 1);

function rgb(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

/** a cubic Bézier sampled to a polyline, drawn up to `t` (0..1) of its length */
function bezier(p: number[][], n = 40): number[][] {
  const [a, b, c, d] = p as [number[], number[], number[], number[]];
  const out: number[][] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t;
    out.push([0, 1].map(k => u * u * u * a[k]! + 3 * u * u * t * b[k]! + 3 * u * t * t * c[k]! + t * t * t * d[k]!));
  }
  return out;
}
function strokePart(ctx: CanvasRenderingContext2D, pts: number[][], t: number): number[] | null {
  if (t <= 0) return null;
  const n = (pts.length - 1) * clamp(t), whole = Math.floor(n);
  ctx.beginPath();
  ctx.moveTo(pts[0]![0]!, pts[0]![1]!);
  for (let i = 1; i <= whole; i++) ctx.lineTo(pts[i]![0]!, pts[i]![1]!);
  let end = pts[whole]!;
  if (whole < pts.length - 1) {
    const a = pts[whole]!, b = pts[whole + 1]!, f = n - whole;
    end = [a[0]! + (b[0]! - a[0]!) * f, a[1]! + (b[1]! - a[1]!) * f];
    ctx.lineTo(end[0]!, end[1]!);
  }
  ctx.stroke();
  return end;
}

/** phones: as on the terminal's rail, each band drops down its own lane left of the names, Core innermost,
    then turns into its row, so no band crosses another */
function lanes(exit: { x: number; y: number }, i: number, gx: number, gy: number): number[][] {
  const lx = gx - 14 - i * 4, top = exit.y + 18 + i * 3, r = 6;
  const pts: number[][] = [];
  const seg = (a: number[], b: number[], n: number) => { for (let k = pts.length ? 1 : 0; k <= n; k++) pts.push([a[0]! + (b[0]! - a[0]!) * k / n, a[1]! + (b[1]! - a[1]!) * k / n]); };
  seg([exit.x, exit.y], [lx, top], 12);
  seg([lx, top], [lx, gy - r], 16);
  seg([lx, gy - r], [lx + r, gy], 3);
  seg([lx + r, gy], [gx, gy], 4);
  return pts;
}

/** a seeded walk: the extension scene's price line is abstract (no axis, no numbers), but the same every visit */
function walk(n: number): number[] {
  let s = 20261007 >>> 0, v = 0;
  const r = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    v += (r() - 0.5) * 0.9 + Math.sin(i / 11) * 0.12;
    out.push(v);
  }
  const lo = Math.min(...out), hi = Math.max(...out);
  return out.map(x => (x - lo) / (hi - lo || 1));
}
const WALK = walk(160);

export interface Story { setData(d: PublicLanding | null): void; setMark(m: Mark | null): void; refresh(): void }

export function createStory(opts: { space: Space | null; reduced: boolean; lang: string; words: Record<string, string> }): Story {
  const html = document.documentElement;
  const $ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document) => r.querySelector<T>(s)!;
  const story = $('.ld-story'), fx = $<HTMLCanvasElement>('.ld-fx');
  const close = $('.ld-close'), hero = $('.ld-hero');
  const sc = (n: number) => $(`.ld-sc${n}`);
  const bands = [...document.querySelectorAll<HTMLElement>('.ld-band')];
  const tags = [...document.querySelectorAll<HTMLElement>('.ld-lvtag')];
  const src = $('.ld-src'), term = $('.ld-term'), termImg = $<HTMLImageElement>('.ld-term-img');
  const panes = [...document.querySelectorAll<HTMLElement>('.ld-pane')];
  const ext = $<HTMLCanvasElement>('.ld-ext-c');
  const ctx = fx.getContext('2d')!, ectx = ext.getContext('2d')!;
  const BANDS = MODULES.map(m => rgb(m.band));

  let mark: Mark | null = null;
  let data: PublicLanding | null = null;
  let core: CoreLayout | null = null;
  let mode: 'stage' | 'flow' = 'flow';
  let W = innerWidth, H = innerHeight, dpr = 1, storyTop = 0;
  let target = 0, shown = 0, raf = 0;

  void fetch(`${import.meta.env.BASE_URL}landing/core.json`)
    .then(r => (r.ok ? r.json() : null)).then((j: CoreLayout | null) => { core = j; request(); }).catch(() => { /* no slide */ });

  /* ---------- layout ---------- */
  function measure() {
    W = innerWidth; H = innerHeight;
    mode = !opts.reduced && W >= 900 && W / H >= 1.1 ? 'stage' : 'flow';
    html.classList.toggle('ld-stage-mode', mode === 'stage');
    html.classList.toggle('ld-flow-mode', mode === 'flow');
    story.style.setProperty('--story', String(STORY));
    storyTop = story.getBoundingClientRect().top + scrollY;
    dpr = Math.min(devicePixelRatio || 1, 2);
    fx.width = Math.round(W * dpr); fx.height = Math.round(H * dpr);
    const er = ext.getBoundingClientRect();
    ext.width = Math.round(er.width * dpr); ext.height = Math.round(er.height * dpr);
    // the extension's candles and its label face are prepared now, not on the frame the scene first shows.
    // Safari has no requestIdleCallback, and `requestIdleCallback?.()` on an undeclared global still throws:
    // it stopped the whole landing script there, the sign-in with it (Log in only reloaded the page)
    const idle = 'requestIdleCallback' in window ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 1);
    idle(() => { drawExtension(0.001); LEVELS.forEach(l => labelPlate(l.key)); });
    if (mode === 'flow') for (const s of document.querySelectorAll<HTMLElement>('.ld-sc')) { s.style.opacity = ''; s.style.visibility = ''; }
    target = shown = posT();
  }
  const posT = () => (scrollY - storyTop) / H;

  /* ---------- the frame ---------- */
  function request() { if (!raf) raf = requestAnimationFrame(frame); }
  function frame() {
    raf = 0;
    target = posT();
    // the drawn position eases toward the scroll (about 120 ms), so wheel steps read as motion
    shown = opts.reduced || Math.abs(target - shown) < 0.0008 ? target : shown + (target - shown) * 0.16;
    render(shown);
    if (shown !== target) request();
  }

  function render(T: number) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const closeK = clamp(1 - close.getBoundingClientRect().top / H);
    if (mode === 'stage') renderStage(T, closeK);
    else renderFlow(closeK);
  }

  /* ---------- stage ---------- */
  function renderStage(T: number, closeK: number) {
    // the camera: in during the hero's exit and scene 2, through the inner triangle in scene 3, back far at the close
    const through = clamp(local(T, SC.s3));
    const far = closeK > 0.02 ? 1 : 0;
    mark?.setStory(far
      ? { dolly: 0, through: 0, far: 1, alpha: smooth(closeK * 1.5) }
      : { dolly: smooth((T + 0.5) / 0.9), through, far: 0, alpha: 1 - smooth((through - 0.62) / 0.12) });
    // the stars: rows in scene 3, dimmed while the levels speak, loosened in scene 8, home at the close
    const align = smooth((T - 1.6) / 0.7) * (1 - 0.8 * smooth(local(T, SC.s8))) * (1 - smooth(closeK * 1.5));
    opts.space?.setAlign(align);
    opts.space?.setDim(0.35 * window_(T, SC.s4[0] + 0.1, SC.s7[1], 0.3));

    show(sc(2), window_(T, -0.2, SC.s3[0] + 0.35, 0.2));
    show(sc(4), window_(T, SC.s4[0], SC.s5[0] + 0.2));
    show(sc(5), window_(T, SC.s5[0], SC.s5[1]));
    show(sc(6), window_(T, SC.s6[0], SC.s6[1]));
    show(sc(7), window_(T, SC.s7[0], SC.s7[1]));
    show(sc(8), window_(T, SC.s8[0], SC.s8[1] + 0.6, 0.15));

    if (T > -0.3 && T < SC.s3[0] + 0.5) drawSpectrum(T);
    if (T > SC.s4[0] && T < SC.s5[0] + 0.9) drawLevels(clamp(local(T, SC.s4)), clamp(local(T, SC.s5)), { left: 0, top: 0, width: W, height: H });
    if (T > SC.s5[0] - 0.1 && T < SC.s5[1] + 0.2) placeTerminal(clamp(local(T, SC.s5)));
    if (T > SC.s6[0] - 0.1 && T < SC.s6[1] + 0.1) placePanes(local(T, SC.s6));
    if (T > SC.s7[0] - 0.1 && T < SC.s7[1] + 0.3) drawExtension(clamp(local(T, SC.s7) * 1.15));
  }
  function show(el: HTMLElement, o: number) {
    el.style.opacity = o.toFixed(3);
    el.style.visibility = o > 0.002 ? 'visible' : 'hidden';
  }

  /** scene 2: the beam enters the mark's left face and leaves as six bands, which land on the names */
  function drawSpectrum(T: number) {
    const p = local(T, SC.s2), fade = 1 - smooth((T - SC.s3[0]) / 0.35);
    let hit: { x: number; y: number }, exit: { x: number; y: number };
    if (mark) { hit = mark.project(HIT.x, HIT.y); exit = mark.project(EXIT.x, EXIT.y); }
    else { hit = { x: W * 0.27, y: H * 0.32 }; exit = { x: W * 0.36, y: H * 0.35 }; }
    spectrum(hit, exit, p, fade, 'right');
  }

  function spectrum(hit: { x: number; y: number }, exit: { x: number; y: number }, p: number, fade: number, dir: 'right' | 'down') {
    if (fade <= 0) { bands.forEach(b => (b.style.opacity = '0')); return; }
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    // the beam
    const pb = expoOut(clamp(p / 0.22));
    if (pb > 0) {
      const x = hit.x * pb;
      const gr = ctx.createLinearGradient(0, 0, Math.max(1, hit.x), 0);
      gr.addColorStop(0, 'rgba(255,255,255,0.12)'); gr.addColorStop(0.6, 'rgba(255,255,255,0.8)'); gr.addColorStop(1, 'rgba(255,255,255,1)');
      ctx.globalAlpha = fade;
      ctx.fillStyle = 'rgba(255,255,255,0.10)';
      ctx.fillRect(0, hit.y - 3, x, 6);
      ctx.fillStyle = gr;
      ctx.fillRect(0, hit.y - 0.5, x, 1);
      // inside the glass: the light crosses to the far face
      const pi = clamp((p - 0.2) / 0.08);
      if (pi > 0) {
        ctx.strokeStyle = `rgba(255,255,255,${0.55 * fade})`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(hit.x, hit.y); ctx.lineTo(lerp(hit.x, exit.x, pi), lerp(hit.y, exit.y, pi)); ctx.stroke();
      }
    }
    // the six bands, 40 ms apart in the terminal's order
    bands.forEach((li, i) => {
      const g = li.querySelector('.ld-band-g')!.getBoundingClientRect();
      const gx = g.left - 12, gy = g.top + g.height / 2;
      const pts = dir === 'right'
        ? bezier([[exit.x, exit.y], [exit.x + (gx - exit.x) * 0.38, exit.y + (i - 2.5) * 6], [exit.x + (gx - exit.x) * 0.55, gy], [gx, gy]])
        : lanes(exit, i, gx, gy);
      const t = expoOut(clamp((p - 0.28 - i * 0.03) / 0.34));
      ctx.globalAlpha = fade;
      ctx.strokeStyle = `rgba(${BANDS[i]},0.22)`; ctx.lineWidth = 6;
      strokePart(ctx, pts, t);
      ctx.strokeStyle = `rgba(${BANDS[i]},1)`; ctx.lineWidth = 1;
      strokePart(ctx, pts, t);
      li.style.opacity = (clamp((t - 0.8) / 0.2) * fade).toFixed(3);
    });
    ctx.restore();
  }

  /** scene 4 (and the slide of scene 5): the call wall, the gamma flip and the put wall as Radar's bands */
  function drawLevels(p4: number, p5: number, r: { left: number; top: number; width: number; height: number }) {
    const lv = data?.levels ?? {};
    const have = LEVELS.every(l => typeof lv[l.key] === 'number');
    const narrow = r.width < 700;
    const x0 = r.left + (narrow ? 16 : r.width * 0.07), x1 = r.left + r.width * (narrow ? 0.62 : 0.74);
    // the range holds all three levels: the flip can sit outside the walls (it did on Oct 16, 731 over 707 to 710)
    const vs = have ? LEVELS.map(l => lv[l.key]!) : [0, 0], top = Math.max(...vs), bot = Math.min(...vs), pad = (top - bot || 1) * 0.12;
    const hi = top + pad, lo = bot - pad;
    const yAt = (k: LevelKey, i: number) => r.top + r.height * (have ? 0.24 + 0.52 * (hi - lv[k]!) / (hi - lo || 1) : [0.34, 0.52, 0.7][i]!);
    // scene 5: the lines slide onto the same levels in the terminal's capture
    const slide = smooth((p5 - 0.1) / 0.4), gone = smooth((p5 - 0.55) / 0.2);
    const fig = slide > 0 ? termImg.getBoundingClientRect() : null;
    const sp = sweepEase(clamp((p4 - 0.05) / 0.45));
    const sx = x0 + sp * (x1 - x0);
    const s4Top = sc(4).getBoundingClientRect().top;
    // the tags keep 46 px apart when two levels nearly meet; their lines stay where the levels are
    const tagY = LEVELS.map((l, i) => yAt(l.key, i));
    const order = tagY.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
    for (let j = 1; j < order.length; j++) if (order[j]!.y - order[j - 1]!.y < 46) order[j]!.y = order[j - 1]!.y + 46;
    order.forEach(o => { tagY[o.i] = o.y; });
    LEVELS.forEach((l, i) => {
      let y = yAt(l.key, i), a = x0, b = sp < 1 ? sx : x1;
      const at = core?.levels[l.key];
      if (fig && at && core) {
        y = lerp(y, fig.top + at.y * fig.height, slide);
        a = lerp(a, fig.left + core.chart.x0 * fig.width, slide);
        b = lerp(b, fig.left + core.chart.x1 * fig.width, slide);
      }
      const k = l.key === 'flip' ? 0.6 : clamp(data?.intensity?.[l.key as 'call_wall'] ?? 0.7, 0.35, 1);
      const dim = 1 - (fig && at ? gone : smooth(p5 / 0.3));
      if (dim <= 0) return;
      // the scene's light: a wide aura under each level (L2), then Radar's band itself
      const c = l.side === 'above' ? rgb(DATA.up) : l.side === 'below' ? rgb(DATA.down) : '242,242,240';
      const ga = (l.side === 'inside' ? 0.09 : 0.14) * (0.55 + 0.45 * k) * dim, gh = 34;
      const gr = ctx.createLinearGradient(0, y - gh, 0, y + gh);
      gr.addColorStop(0, `rgba(${c},0)`); gr.addColorStop(0.5, `rgba(${c},${ga})`); gr.addColorStop(1, `rgba(${c},0)`);
      ctx.fillStyle = gr;
      ctx.fillRect(a, y - gh, b - a, gh * 2);
      levelBand(ctx, { y1: y, y2: y, ym: y, k, side: l.side, lit: l.key === 'flip' }, dim, a, b);
      if (sp > 0 && sp < 1) levelFlare(ctx, y, k, l.side, sx);
      // the tags ride with their lines until the slide
      const tag = tags[i]!;
      tag.style.top = `${(slide > 0 ? y : tagY[i]!) - s4Top}px`;
      tag.style.opacity = have ? (smooth((p4 - 0.4) / 0.2) * (1 - smooth(p5 / 0.15))).toFixed(3) : '0';
    });
    if (sp > 0 && sp < 1) sweepLine(ctx, sx, r.top + r.height * 0.16, r.height * 0.68);
  }

  /** scene 5: the capture arrives from a tilt and settles flat; its own light comes up as our lines land */
  function placeTerminal(p: number) {
    const e = expoOut(clamp(p / 0.45));
    termImg.style.transform = `perspective(1600px) rotateX(${14 * (1 - e)}deg) rotateY(${-10 * (1 - e)}deg) translateZ(${-220 * (1 - e)}px)`;
    termImg.style.opacity = e.toFixed(3);
    termImg.style.filter = `brightness(${lerp(0.5, 1, smooth((p - 0.45) / 0.25)).toFixed(3)})`;
    term.style.setProperty('--cap', smooth((p - 0.55) / 0.15).toFixed(3));
  }

  /** scene 6: three real screens as panes of glass on a fan. The focus moves from one to the next with a hold on
      each; the pane in front is flat and lit (its module's band on its top edge), the others turn away into depth,
      dimmed, so the three read as one instrument seen from three sides. */
  function placePanes(p: number) {
    // the focus: 0, 1, 2 with holds; the move to the next takes the last 40% of each third
    const third = Math.min(2, Math.floor(p * 3));
    const f = Math.min(2, third + smooth(clamp((p * 3 - third - 0.6) / 0.4)));
    const enter = expoOut(clamp(p / 0.12)), leave = smooth((p - 0.92) / 0.08);
    panes.forEach((el, i) => {
      const d = i - f, a = Math.abs(d);
      const x = d * 62, z = -a * 320 - (1 - enter) * 400, ry = -d * 26, sc = 1 - Math.min(a, 1.5) * 0.08;
      el.style.transform = `translate3d(${x.toFixed(2)}%, ${(a * 3).toFixed(2)}%, ${z.toFixed(1)}px) rotateY(${ry.toFixed(2)}deg) scale(${sc.toFixed(3)})`;
      el.style.opacity = (clamp(1.6 - a) * enter * (1 - leave)).toFixed(3);
      el.style.filter = `brightness(${(1 - Math.min(a, 1) * 0.55).toFixed(3)})`;   // no blur: three big images under a blur cost frames
      el.style.zIndex = String(10 - Math.round(a * 3));
      el.style.setProperty('--f', clamp(1 - a * 1.6).toFixed(3));
      el.style.visibility = a > 1.65 || p >= 1 ? 'hidden' : 'visible';
    });
  }

  /** scene 7: a dark chart we draw, as PRISM for TradingView shows it (3.1, the lines style): candles, then the
      call wall, the gamma flip and the put wall drawn in as thin translucent lines, each named at the right */
  const EXT = { call_wall: { c: '61,196,142', name: 'Call wall', mk: '▲▲▲' }, flip: { c: '232,162,58', name: 'Gamma flip', mk: '' }, put_wall: { c: '228,98,91', name: 'Put wall', mk: '▼▼' } } as const;
  const candles = document.createElement('canvas');
  let candleKey = '';
  function drawExtension(p: number) {
    const w = ext.width / dpr, h = ext.height / dpr;
    if (!w || !h) return;
    const c = ectx;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, w, h);
    const L = 20, R = w - 64, T = 24, B = h - 28;
    c.fillStyle = 'rgba(255,255,255,0.045)';
    for (let i = 1; i < 5; i++) c.fillRect(L, Math.round(T + ((B - T) * i) / 5) - 0.5, R - L, 1);
    for (let i = 1; i < 8; i++) c.fillRect(Math.round(L + ((R - L) * i) / 8) - 0.5, T, 1, B - T);
    c.fillStyle = 'rgba(255,255,255,0.08)';
    c.fillRect(R, T, 1, B - T);
    // candles from the walk, four points each (no axis numbers: the price is abstract), drawn once per size into
    // their own canvas; the scroll only reveals more of it
    const yOf = (v: number) => T + (B - T) * (0.12 + 0.7 * (1 - v));
    const n = Math.floor(WALK.length / 4), cw = (R - L - 40) / n;
    const key = `${w}x${h}x${dpr}`;
    if (candleKey !== key) {
      candleKey = key;
      candles.width = Math.round(w * dpr); candles.height = Math.round(h * dpr);
      const k2 = candles.getContext('2d')!;
      k2.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (let k = 0; k < n; k++) {
        const q = WALK.slice(k * 4, k * 4 + 5), o = q[0]!, cl = q[q.length - 1]!, hi = Math.max(...q), lo = Math.min(...q);
        const x = L + k * cw + cw / 2, up = cl >= o;
        k2.fillStyle = up ? 'rgba(61,196,142,0.85)' : 'rgba(228,98,91,0.85)';
        k2.fillRect(Math.round(x) - 0.5, yOf(hi), 1, yOf(lo) - yOf(hi));
        const y0 = yOf(Math.max(o, cl)), y1 = yOf(Math.min(o, cl));
        k2.fillRect(x - cw * 0.32, y0, cw * 0.64, Math.max(1, y1 - y0));
      }
    }
    const shown = Math.round(n * smooth(clamp(p / 0.45)));
    if (shown > 0) {
      const sw = L + shown * cw;
      c.drawImage(candles, 0, 0, sw * dpr, candles.height, 0, 0, sw, h);
    }
    // the levels sit where the walk turned, at fixed fractions of the plot
    const ys = { call_wall: T + (B - T) * 0.2, flip: T + (B - T) * 0.56, put_wall: T + (B - T) * 0.86 };
    const lp = smooth(clamp((p - 0.4) / 0.4));
    LEVELS.forEach((l, i) => {
      const k = smooth(clamp(lp * 1.6 - i * 0.3));
      if (k <= 0) return;
      const e = EXT[l.key], y = Math.round(ys[l.key]) + 0.5;
      c.strokeStyle = `rgba(${e.c},${(0.62 * k).toFixed(3)})`; c.lineWidth = 1;
      c.beginPath(); c.moveTo(L, y); c.lineTo(L + (R - L) * k, y); c.stroke();
      // the name, painted once into its own small canvas (text on every frame cost the scroll a frame)
      const pl = labelPlate(l.key);
      c.globalAlpha = k;
      c.drawImage(pl, R - 6 - pl.width / dpr, y - 6 - 14, pl.width / dpr, pl.height / dpr);
      c.globalAlpha = 1;
    });
  }
  const plates: Partial<Record<LevelKey, HTMLCanvasElement>> = {};
  let plateDpr = 0;
  function labelPlate(key: LevelKey): HTMLCanvasElement {
    if (plateDpr !== dpr) { for (const k of Object.keys(plates)) delete plates[k as LevelKey]; plateDpr = dpr; }
    const hit = plates[key];
    if (hit) return hit;
    const e = EXT[key], text = `${e.name}${e.mk ? ' ' + e.mk : ''}`;
    const pc = document.createElement('canvas'), x = pc.getContext('2d')!;
    const font = 'italic 500 11px Switzer, sans-serif';
    x.font = font;
    const w = Math.ceil(x.measureText(text).width) + 4;
    pc.width = Math.ceil(w * dpr); pc.height = Math.ceil(18 * dpr);
    x.setTransform(dpr, 0, 0, dpr, 0, 0);
    x.font = font; x.textBaseline = 'alphabetic'; x.textAlign = 'left';
    x.fillStyle = `rgba(${e.c},0.9)`;
    x.fillText(text, 2, 14);
    plates[key] = pc;
    return pc;
  }

  /* ---------- flow ---------- */
  function renderFlow(closeK: number) {
    // each scene plays from the moment it enters to when its top reaches 35% of the viewport
    const pIn = (el: Element) => (opts.reduced ? 1 : clamp((H - el.getBoundingClientRect().top) / (H * 0.65)));
    const heroOut = clamp(-hero.getBoundingClientRect().top / (H * 0.8));
    mark?.setStory(closeK > 0.02
      ? { dolly: 0, through: 0, far: 1, alpha: smooth(closeK * 1.5) }
      : { dolly: 0, through: 0, far: 0, alpha: 1 - smooth(heroOut) });
    const s4 = sc(4), s8 = sc(8);
    const s4r = s4.getBoundingClientRect();
    opts.space?.setAlign(opts.reduced ? 0 : smooth(pIn(s4) * 1.2) * (1 - 0.8 * smooth(pIn(s8))) * (1 - smooth(closeK * 1.5)));
    opts.space?.setDim(0);

    const s2 = sc(2), s2r = s2.getBoundingClientRect();
    if (s2r.bottom > 0 && s2r.top < H) {
      const m = $('.ld-sc2-mark', s2).getBoundingClientRect();
      const at = (x: number, y: number) => ({ x: m.left + ((x + 1.08) / 2.16) * m.width, y: m.top + ((-y + 1.2347) / 1.8921) * m.height });
      spectrum(at(HIT.x, HIT.y), at(EXIT.x, EXIT.y), pIn(s2) * 1.3, 1, 'down');
    }
    if (s4r.bottom > 0 && s4r.top < H) {
      const plot = $('.ld-lv-plot', s4).getBoundingClientRect();
      drawLevels(pIn(s4) * 1.2, 0, plot);
    }
    termImg.style.transform = ''; termImg.style.opacity = ''; termImg.style.filter = '';
    term.style.setProperty('--cap', '1');
    // the flow: one pane after the other, each rising flat into its light as it enters
    panes.forEach(el => {
      const q = expoOut(pIn(el));
      el.style.transform = opts.reduced ? '' : `perspective(1600px) rotateX(${(10 * (1 - q)).toFixed(2)}deg) translateY(${(40 * (1 - q)).toFixed(1)}px)`;
      el.style.opacity = opts.reduced ? '' : q.toFixed(3);
      el.style.filter = ''; el.style.zIndex = ''; el.style.visibility = '';
      el.style.setProperty('--f', opts.reduced ? '1' : q.toFixed(3));
    });
    const s7 = sc(7), s7r = s7.getBoundingClientRect();
    if (s7r.bottom > 0 && s7r.top < H) drawExtension(pIn(s7) * 1.1);
  }

  /* ---------- data ---------- */
  function setData(d: PublicLanding | null) {
    data = d;
    const lv = d?.levels ?? {};
    const close_ = d?.close;
    LEVELS.forEach((l, i) => {
      const v = lv[l.key], tag = tags[i]!;
      const out = tag.querySelector('.ld-lvtag-v')!;
      if (typeof v !== 'number') { out.textContent = ''; return; }
      const dist = typeof close_ === 'number' && close_ > 0 ? ((v - close_) / close_) * 100 : null;
      out.textContent = price(v) + (dist === null ? '' : `  ${dist >= 0 ? '+' : MINUS}${Math.abs(dist).toFixed(2)}%`);
      const from = document.createElement('span');
      from.className = 'ld-lvtag-from';
      from.textContent = dist === null ? '' : ` ${opts.words['s4.fromClose'] ?? ''}`;
      out.append(from);
      const fut = tag.querySelector('.ld-lvtag-f');
      if (fut) fut.textContent = l.key === 'call_wall' && d?.futures ? `${d.futures.symbol} ${price(d.futures.call_wall)}` : '';
    });
    if (d?.session) {
      const when = opts.lang === 'fr'
        ? new Intl.DateTimeFormat('fr-FR', { timeZone: 'America/New_York', weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(d.session + 'T12:00:00Z'))
        : day(d.session);
      src.textContent = (opts.words['s4.source'] ?? '').replace('{day}', when);
    }
    html.classList.toggle('ld-has-levels', LEVELS.every(l => typeof lv[l.key] === 'number'));
    request();
  }

  if (opts.reduced) {
    html.classList.add('ld-reduced');
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }), { threshold: 0.15 });
    document.querySelectorAll('.ld-sc').forEach(el => io.observe(el));
  }
  measure();
  addEventListener('resize', () => { measure(); request(); });
  addEventListener('scroll', request, { passive: true });
  // the images change the layout as they arrive
  addEventListener('load', () => { measure(); request(); });
  request();

  return {
    setData,
    setMark(m) { mark = m; request(); },
    refresh() { measure(); request(); },
  };
}
